import React, { useState, useEffect } from 'react';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave,
    faRobot,
    faGraduationCap,
    faUser,
    faEnvelope,
    faPhone,
    faLock,
    faChevronLeft,
    faKeyboard
} from '@fortawesome/free-solid-svg-icons';
import FileUploader from '../Shared/FileUploader';
import Loader from '../Shared/Loader';
import DropdownBox from '../Shared/DropdownBox';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';

const validationSchema = Yup.object().shape({
    fullName: Yup.string()
        .required('الاسم بالكامل مطلوب')
        .min(3, 'الاسم يجب أن يكون 3 أحرف على الأقل'),
    email: Yup.string()
        .email('البريد الإلكتروني غير صالح')
        .required('البريد الإلكتروني مطلوب'),
    password: Yup.string()
        .min(6, 'كلمة المرور يجب أن لا تقل عن 6 أحرف'),
    phone: Yup.string()
        .matches(/^[0-9]+$/, 'رقم الهاتف يجب أن يحتوي على أرقام فقط')
        .min(11, 'رقم الهاتف يجب أن يكون 11 رقماً على الأقل'),
    qualification: Yup.string(),
    material_id: Yup.string()
        .required('يرجى اختيار المادة'),
    gender: Yup.string()
        .required('يرجى اختيار الجنس'),
    selectedClasses: Yup.array()
        .min(1, 'يرجى اختيار فصل دراسي واحد على الأقل')
});

const AddTeacher = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [aboutLoading, setAboutLoading] = useState(false);
    const [userClasses, setUserClasses] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [profilePicture, setProfilePicture] = useState(null);

    const GENDER_OPTIONS = [
        { id: '1', name: 'Male', description: 'ذكر' },
        { id: '2', name: 'Female', description: 'أنثى' },
    ];

    const [selectedGender, setSelectedGender] = useState(GENDER_OPTIONS[0]);
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const [classesRes, materialsRes] = await Promise.all([
                    ApiServices.get('Lookup/UserClasses'),
                    ApiServices.get('Lookup/Materials')
                ]);
                setUserClasses(classesRes);
                setMaterials(materialsRes);
            } catch (error) {
                toastHelper.error('فشل في تحميل البيانات المساعدة');
            }
        };
        fetchLookups();
    }, []);

    // Update selectedMaterial when materials are loaded
    useEffect(() => {
        if (materials.length > 0 && !selectedMaterial) {
            setSelectedMaterial({
                id: materials[0].id,
                name: materials[0].name,
                description: materials[0].name,
            });
        }
    }, [materials, selectedMaterial]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePicture(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleGenerateAbout = async (qualification, setFieldValue) => {
        if (!qualification) {
            toastHelper.warning('يرجى إدخال المؤهل أولاً');
            return;
        }
        setAboutLoading(true);
        try {
            const response = await ApiServices.post(
                'TeacherAbout/GenerateContent',
                JSON.stringify(qualification),
                false,
                80000,
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            setFieldValue('about', response);
            toastHelper.success('تم توليد النبذة بنجاح');
        } catch (error) {
            toastHelper.error('فشل في توليد النبذة');
        } finally {
            setAboutLoading(false);
        }
    };

    const initialValues = {
        fullName: '',
        email: '',
        password: '',
        phone: '',
        about: '',
        qualification: '',
        gender: '1',
        material_id: '',
        selectedClasses: []
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const data = new FormData();
            data.append('fullName', values.fullName);
            data.append('email', values.email);
            data.append('password', values.password);
            data.append('phone', values.phone || '');
            data.append('about', values.about || '');
            data.append('qualification', values.qualification || '');
            data.append('gender', values.gender);
            data.append('material_id', values.material_id);

            if (profilePicture) {
                data.append('profile_picture', profilePicture);
            }

            values.selectedClasses.forEach(id => {
                data.append('user_class', id);
            });

            await ApiServices.post('User/Teacher/Add', data, true);
            toastHelper.success('تم إضافة المعلم بنجاح');
            navigate('/admin/dashboard');
        } catch (error) {
            toastHelper.error(error.msg || 'فشل في إضافة المعلم');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loader text="جاري إضافة المعلم..." />;

    return (
        <div className="add-teacher-page">
            <div className="form-container">
                <div className="form-header">
                    <button className="back-btn" onClick={() => window.history.back()}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                        <span>عودة</span>
                    </button>
                    <h1>إضافة معلم جديد</h1>
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form className="teacher-form">
                            <div className="form-grid">
                                <div className="input-group">
                                    <label><FontAwesomeIcon icon={faUser} /> الاسم بالكامل *</label>
                                    <Field
                                        name="fullName"
                                        placeholder="أدخل الاسم الرباعي"
                                        className={errors.fullName && touched.fullName ? 'input-error' : ''}
                                    />
                                    <ErrorMessage name="fullName" component="div" className="field-error" />
                                </div>

                                <div className="input-group">
                                    <label><FontAwesomeIcon icon={faEnvelope} /> البريد الإلكتروني *</label>
                                    <Field
                                        name="email"
                                        type="email"
                                        placeholder="email@example.com"
                                        className={errors.email && touched.email ? 'input-error' : ''}
                                    />
                                    <ErrorMessage name="email" component="div" className="field-error" />
                                </div>

                                <div className="input-group">
                                    <label><FontAwesomeIcon icon={faLock} /> كلمة المرور</label>
                                    <Field
                                        name="password"
                                        type="password"
                                        placeholder="إتركها فارغة للتوليد التلقائي"
                                        className={errors.password && touched.password ? 'input-error' : ''}
                                    />
                                    <ErrorMessage name="password" component="div" className="field-error" />
                                </div>

                                <div className="input-group">
                                    <label><FontAwesomeIcon icon={faPhone} /> رقم الهاتف</label>
                                    <Field
                                        name="phone"
                                        placeholder="01xxxxxxxxx"
                                        className={errors.phone && touched.phone ? 'input-error' : ''}
                                    />
                                    <ErrorMessage name="phone" component="div" className="field-error" />
                                </div>

                                <div className="input-group">
                                    <label><FontAwesomeIcon icon={faGraduationCap} /> المؤهل الدراسي</label>
                                    <Field
                                        name="qualification"
                                        placeholder="مثال: ليسانس آداب لغة عربية"
                                    />
                                </div>

                                <div className="input-row">
                                    <div className="input-group half">
                                        <DropdownBox
                                            label="الجنس"
                                            options={GENDER_OPTIONS}
                                            selectedOption={selectedGender}
                                            onSelect={(option) => {
                                                setSelectedGender(option);
                                                setFieldValue('gender', option.id);
                                            }}
                                            title="اختر الجنس"
                                            required={true}
                                            fullWidth={true}
                                        />
                                    </div>
                                    <div className="input-group half">
                                        <DropdownBox
                                            label="المادة *"
                                            options={materials.map(m => ({
                                                id: m.id.toString(),
                                                name: m.name,
                                                description: m.name,
                                            }))}
                                            selectedOption={selectedMaterial}
                                            onSelect={(option) => {
                                                setSelectedMaterial(option);
                                                setFieldValue('material_id', option.id);
                                            }}
                                            title="اختر المادة"
                                            required={true}
                                            fullWidth={true}
                                        />
                                        {errors.material_id && touched.material_id && (
                                            <div className="field-error">{errors.material_id}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="input-group full-width">
                                    <label>الفصول الدراسية *</label>
                                    <div className="classes-grid">
                                        {userClasses.map(c => {
                                            const isSelected = values.selectedClasses.includes(c.id);
                                            return (
                                                <div
                                                    key={c.id}
                                                    className={`class-chip ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        const nextClasses = isSelected
                                                            ? values.selectedClasses.filter(id => id !== c.id)
                                                            : [...values.selectedClasses, c.id];
                                                        setFieldValue('selectedClasses', nextClasses);
                                                    }}
                                                >
                                                    {c.name}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <ErrorMessage name="selectedClasses" component="div" className="field-error" />
                                </div>

                                <div className="input-group full-width">
                                    <label>الصورة الشخصية</label>
                                    <FileUploader
                                        previewUrl={previewUrl}
                                        onFileChange={handleFileChange}
                                        placeholder="اسحب صورة المعلم هنا"
                                    />
                                </div>
                            </div>

                            <div className="about-section">
                                <div className="input-group">
                                    <label className="flex-label">
                                        <span><FontAwesomeIcon icon={faKeyboard} /> نبذة عن المعلم</span>
                                        <button
                                            type="button"
                                            className="ai-gen-btn"
                                            onClick={() => handleGenerateAbout(values.qualification, setFieldValue)}
                                            disabled={aboutLoading}
                                        >
                                            {aboutLoading ? 'جاري التوليد...' : <><FontAwesomeIcon icon={faRobot} /> توليد بالذكاء الاصطناعي</>}
                                        </button>
                                    </label>
                                    <Field
                                        as="textarea"
                                        name="about"
                                        placeholder="اكتب نبذة عن المعلم أو استخدم الذكاء الاصطناعي للتوليد.."
                                        rows="5"
                                    />
                                </div>
                            </div>

                            <div className="form-footer">
                                <button type="submit" className="submit-btn" disabled={loading}>
                                    <FontAwesomeIcon icon={faSave} />
                                    <span>حفظ بيانات المعلم</span>
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            <style>{`
                .add-teacher-page {
                    padding: 20px;
                    background: #f8fafc;
                    min-height: 100vh;
                    direction: rtl;
                      font-family: "Zain", sans-serif;
                }
                .form-container {
                    max-width: 900px;
                    margin: 0 auto;
                    background: white;
                    padding: 20px;
                    border-radius: 20px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                }
                .form-header {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #f1f5f9;
                    padding-bottom: 20px;
                    flex-wrap: wrap;
                }
                .back-btn {
                    background: #f1f5f9;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    color: #64748b;
                }
                .form-header h1 {
                    margin: 0;
                    font-size: clamp(1.4rem, 4vw, 1.8rem);
                    color: #1e293b;
                    flex: 1;
                }
                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .input-group {
                    margin-bottom: 0;
                    position: relative;
                }
                .input-group.full-width {
                    grid-column: 1 / -1;
                }
                .input-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 700;
                    color: #475569;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: clamp(0.9rem, 2.5vw, 1rem);
                }
                .flex-label {
                    justify-content: space-between;
                }
                .input-group input, 
                .input-group select, 
                .input-group textarea {
                    width: 100%;
                    padding: 12px 15px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 1rem;
                    transition: all 0.2s;
                      font-family: "Zain", sans-serif;
                    box-sizing: border-box;
                }
                .input-group input:focus,
                .input-group select:focus,
                .input-group textarea:focus {
                    border-color: #6366f1;
                    outline: none;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
                }
                .input-group .input-error {
                    border-color: #ef4444;
                }
                .field-error {
                    color: #ef4444;
                    font-size: 0.85rem;
                    margin-top: 5px;
                    font-weight: 600;
                }
                .input-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                    grid-column: 1 / -1;
                }
                .half {
                    flex: 1;
                }
                .classes-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
                    gap: 10px;
                }
                .class-chip {
                    padding: 10px;
                    background: #f1f5f9;
                    border: 2px solid transparent;
                    border-radius: 10px;
                    text-align: center;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: all 0.2s;
                      font-family: "Zain", sans-serif;
                    word-break: break-word;
                }
                .class-chip.selected {
                    background: #eef2ff;
                    border-color: #6366f1;
                    color: #6366f1;
                    font-weight: 700;
                }
                .ai-gen-btn {
                    background: #f1f5f9;
                    color: #6366f1;
                    border: none;
                    padding: 5px 12px;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                      font-family: "Zain", sans-serif;
                    white-space: nowrap;
                }
                .ai-gen-btn:hover {
                    background: #eef2ff;
                }
                .about-section {
                    margin-top: 30px;
                    padding-top: 20px;
                    border-top: 2px solid #f1f5f9;
                }
                .form-footer {
                    margin-top: 40px;
                    border-top: 2px solid #f1f5f9;
                    padding-top: 30px;
                    display: flex;
                    justify-content: center;
                }
                .submit-btn {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    color: white;
                    border: none;
                    padding: 12px 30px;
                    border-radius: 15px;
                    font-size: clamp(0.9rem, 2.5vw, 1.1rem);
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    transition: all 0.3s ease;
                    box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);
                    flex-wrap: wrap;
                    justify-content: center;
                }
                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 25px -5px rgba(99, 102, 241, 0.4);
                }
                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                /* Tablet Responsiveness */
                @media (max-width: 768px) {
                    .form-container {
                        padding: 15px;
                    }
                    .form-header {
                        gap: 10px;
                    }
                    .form-header h1 {
                        flex-basis: 100%;
                    }
                    .input-row {
                        grid-template-columns: 1fr;
                    }
                    .classes-grid {
                        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
                    }
                }

                /* Mobile Responsiveness */
                @media (max-width: 480px) {
                    .add-teacher-page {
                        padding: 10px;
                    }
                    .form-container {
                        padding: 15px;
                        border-radius: 15px;
                    }
                    .form-grid {
                        grid-template-columns: 1fr;
                        gap: 15px;
                    }
                    .input-group label {
                        font-size: 0.9rem;
                    }
                    .input-group input, 
                    .input-group select, 
                    .input-group textarea {
                        padding: 10px 12px;
                        font-size: 16px;
                    }
                    .classes-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .class-chip {
                        padding: 8px;
                        font-size: 0.85rem;
                    }
                    .submit-btn {
                        padding: 10px 20px;
                        font-size: 0.95rem;
                    }
                }`}</style>
        </div>
    );
};

export default AddTeacher;
